/**
 * Generic filter drawer wrapper that provides the standard Outlet/useOutletContext
 * pattern for filter drawers. Renders RpaDrawer with a footer containing Apply and
 * Clear All buttons wired to the form via formId.
 */
import { Button } from "@blurb/codex-react";
import { Outlet, useOutletContext } from "react-router-dom";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { SearchInput } from "../../configuration/filters/searchInput";
import { useFilterStyles } from "./filterStyles";

export type FilterDrawerContext<TField extends string> = {
  initialFilters: SearchInput<TField>[];
  onClose: () => void;
  onApply: (filters: SearchInput<TField>[]) => void;
};

export function useFilterDrawerContext<TField extends string>() {
  return useOutletContext<FilterDrawerContext<TField>>();
}

type FilterDrawerProps<TField extends string> = {
  formId: string;
  isOpened: boolean;
  filters: SearchInput<TField>[];
  onClose: () => void;
  onApply: (filters: SearchInput<TField>[]) => void;
};

export function FilterDrawer<TField extends string>({
  formId,
  isOpened,
  filters,
  onClose,
  onApply,
}: FilterDrawerProps<TField>) {
  const { classes } = useFilterStyles();

  const outletContext: FilterDrawerContext<TField> = {
    initialFilters: filters,
    onApply,
    onClose,
  };

  return (
    <RpaDrawer title="Filters" opened={isOpened} onClose={onClose}>
      <Outlet context={outletContext} />
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" fullWidth intent="primary" type="submit" form={formId}>
            Apply
          </Button>
          <Button variant="text" intent="primary" type="reset" form={formId}>
            Clear All
          </Button>
        </div>
      </RpaDrawer.Footer>
    </RpaDrawer>
  );
}
